import {
  Job,
  JobStatusEnum,
  useJobCancelMutation,
  useJobCreateDeliveryRequestMutation,
  useJobRetryMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import { useTranslation } from 'react-i18next';

type Args = {
  job: Job;
  removeJobId: (jid: string) => void;
  restart: () => void;
  stop: () => void;
};

export default function useActions({ job, removeJobId, stop, restart }: Args) {
  const {
    canCancel,
    canCreateDeliveryRequest,
    deliveryRequest,
    jid,
    status,
    stop: isStopped,
  } = job;
  const { t } = useTranslation();

  const canRemoveJob =
    status === JobStatusEnum.Failed ||
    status === JobStatusEnum.Stopped ||
    status === JobStatusEnum.Complete ||
    status === JobStatusEnum.Interrupted;
  const canCancelJob = canCancel?.value;
  const canSendByEmail = canCreateDeliveryRequest?.value || deliveryRequest;

  const [cancelJobMutation, cancelJobMutationResult] = useJobCancelMutation();
  const [retryJobMutation] = useJobRetryMutation();
  const [createDeliveryRequestMutation, createDeliveryRequestMutationResult] =
    useJobCreateDeliveryRequestMutation();

  const { loading: cancelJobMutationLoading } = cancelJobMutationResult;
  const { loading: createDeliveryRequestMutationLoading, data } =
    createDeliveryRequestMutationResult;
  const isCreateDeliveryRequestCalled = !!data?.data;

  const onRemoveHandler = () => {
    if (jid && (isStopped || canRemoveJob)) {
      removeJobId(jid);
    } else if (jid) {
      stop();
      cancelJobMutation({
        variables: { input: { jid } },
        onCompleted: (res) => {
          console.log('✅ Cancel job: ', res?.data);
          if (res?.data) {
            removeJobId(jid);
            restart();
          }
        },
        onError: (error) => {
          toast({ content: getErrorMessage(error, t) });
          restart();
        },
      });
    }
  };

  const onRetryHandler = () => {
    if (jid) {
      stop();
      retryJobMutation({
        variables: {
          input: {
            jid,
          },
        },
        onCompleted: (res) => {
          console.log('✅ Retry job: ', res?.data);
          if (res?.data) {
            restart();
          }
        },
        onError: (error) => {
          toast({ content: getErrorMessage(error, t) });
          restart();
        },
      });
    }
  };

  const onCreateDeliveryRequestHandler = () => {
    if (jid) {
      createDeliveryRequestMutation({
        variables: {
          input: {
            jid,
          },
        },
        onCompleted: (res) => {
          console.log('✅ Create Delivery Request: ', res?.data);
        },
        onError: (error) => {
          toast({ content: getErrorMessage(error, t) });
        },
      });
    }
  };

  return {
    onRemoveHandler,
    onRetryHandler,
    onCreateDeliveryRequestHandler,
    cancelJobMutationLoading,
    createDeliveryRequestMutationLoading,
    isCreateDeliveryRequestCalled,
    canRemoveJob,
    canCancelJob,
    canSendByEmail,
  };
}
